/**
 * ReplaceBatchModal.
 *
 * Admin-only modal that replaces an ACTIVE batch with a new one. The old
 * batch transitions to REPLACED (terminal status); the new batch carries
 * `replacesBatchId` back to the original so audit history is traceable.
 *
 * Used when a batch fails inspection definitively, a supplier defaults, or
 * the original was over-ordered and the remainder is being re-sourced. The
 * "cancellation reason" applies to the old batch (it's stored alongside the
 * REPLACED status); the new batch starts fresh at stage 1 (orderPlacedDate).
 *
 * Server-side route at POST /api/projects/.../batches/:batchId/replace.
 */

import { useState, type FormEvent } from 'react';
import { todayPktIsoDate } from '@procurement/shared';
import { DmyDateInput } from '@/components/forms/dmy-date-input';
import { Modal } from '@/components/ui/modal';
import { useReplaceBatch, useSuppliersList, type OrderBatch } from '@/lib/pipeline-queries';

interface ReplaceBatchModalProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  itemId: string;
  batch: OrderBatch;
}

// Patch E18 — PKT date default. UTC-naive `new Date().toISOString()` was
// rolling tomorrow's date for late-night operators in Pakistan.
const todayIso = todayPktIsoDate;

export function ReplaceBatchModal({
  open,
  onClose,
  projectId,
  itemId,
  batch,
}: ReplaceBatchModalProps) {
  const suppliersQ = useSuppliersList();
  const replace = useReplaceBatch(projectId, itemId);

  const [supplierId, setSupplierId] = useState<string>(batch.supplierId);
  const [quantity, setQuantity] = useState<string>(String(batch.quantity));
  const [orderPlacedDate, setOrderPlacedDate] = useState<string>(todayIso());
  const [expectedArrivalDate, setExpectedArrivalDate] = useState<string>('');
  const [cancellationReason, setCancellationReason] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setSupplierId(batch.supplierId);
    setQuantity(String(batch.quantity));
    setOrderPlacedDate(todayIso());
    setExpectedArrivalDate('');
    setCancellationReason('');
    setError(null);
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    const qtyNum = Number(quantity);
    if (!Number.isFinite(qtyNum) || qtyNum <= 0 || !Number.isInteger(qtyNum)) {
      setError('Quantity must be a positive integer');
      return;
    }
    if (!supplierId) {
      setError('Replacement supplier is required');
      return;
    }
    if (!orderPlacedDate) {
      setError('Order placed date is required');
      return;
    }
    if (!expectedArrivalDate) {
      setError('Expected arrival date is required');
      return;
    }
    if (expectedArrivalDate < orderPlacedDate) {
      setError('Expected arrival cannot precede the order placed date');
      return;
    }
    if (!cancellationReason.trim()) {
      setError('Cancellation reason is required (it will appear in audit history)');
      return;
    }

    try {
      await replace.mutateAsync({
        batchId: batch.id,
        rowVersion: batch.rowVersion,
        cancellationReason: cancellationReason.trim(),
        replacement: {
          supplierId,
          quantity: qtyNum,
          orderPlacedDate,
          expectedArrivalDate,
        },
      });
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Replace failed');
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Replace batch"
      description="The old batch will be marked REPLACED and a new batch created in its place, starting at stage 1."
      size="md"
      footer={
        <>
          <button
            type="button"
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={replace.isPending}
            className="rounded-sm border border-border-default bg-surface px-3 py-1.5 text-sm text-secondary hover:border-border-strong disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="replace-batch-form"
            disabled={replace.isPending}
            className="rounded-sm bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-strong disabled:opacity-50"
          >
            {replace.isPending ? 'Replacing…' : 'Replace batch'}
          </button>
        </>
      }
    >
      <form id="replace-batch-form" onSubmit={submit} className="flex flex-col gap-3">
        {/* Old batch summary */}
        <div className="rounded-sm border border-border-default bg-subtle/40 px-3 py-2 text-xs">
          <div className="text-tertiary">Replacing</div>
          <div className="mt-0.5 text-secondary">
            {batch.supplierNameSnapshot} · {batch.quantity} units · ordered {batch.orderPlacedDate}
          </div>
        </div>

        {/* Cancellation reason */}
        <div>
          <label className="text-sm font-medium text-primary" htmlFor="reason">
            Cancellation reason
          </label>
          <textarea
            id="reason"
            rows={2}
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            placeholder="Why is this batch being replaced?"
            className="mt-1 w-full resize-none rounded-sm border border-border-default bg-surface px-2 py-1.5 text-sm text-primary focus:border-accent focus:outline-none"
          />
        </div>

        {/* Replacement supplier */}
        <div>
          <label className="text-sm font-medium text-primary" htmlFor="supplier">
            Replacement supplier
          </label>
          <select
            id="supplier"
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
            className="mt-1 w-full rounded-sm border border-border-default bg-surface px-2 py-1.5 text-sm text-primary focus:border-accent focus:outline-none"
          >
            <option value="">— Select supplier —</option>
            {(suppliersQ.data ?? [])
              .filter((s) => s.isActive)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
          </select>
        </div>

        {/* Quantity */}
        <div>
          <label className="text-sm font-medium text-primary" htmlFor="qty">
            Replacement quantity
          </label>
          <input
            id="qty"
            type="number"
            min="1"
            step="1"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="mt-1 w-full rounded-sm border border-border-default bg-surface px-2 py-1.5 text-sm text-primary focus:border-accent focus:outline-none"
          />
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium text-primary" htmlFor="placed">
              Order placed
            </label>
            <DmyDateInput
              id="placed"
              maxDate={todayIso()}
              value={orderPlacedDate}
              onChange={setOrderPlacedDate}
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-primary" htmlFor="eta">
              Expected arrival
            </label>
            <DmyDateInput
              id="eta"
              minDate={orderPlacedDate || undefined}
              value={expectedArrivalDate}
              onChange={setExpectedArrivalDate}
              className="mt-1"
            />
          </div>
        </div>

        {error ? (
          <div className="rounded-sm border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger-strong">
            {error}
          </div>
        ) : null}
      </form>
    </Modal>
  );
}
